/**
 * Cart-reserve builder — the boundary between cart producers
 * (`components/cart/cart.tsx`, `hooks/use-checkout-session.ts`) and the HTTP
 * route `app/api/cart/reserve/route.ts`. Mirror of
 * `apps/mobile/lib/api-kraahl.ts::reserveCartStock`.
 *
 *   components/cart/cart.tsx          ┐
 *   hooks/use-checkout-session.ts     ┴── intent ──▶ reserveCart
 *                                                          │
 *                                                          ├── safeParse (boundary)
 *                                                          ├── seal → Built<CartReserveRequest>
 *                                                          └── postJson ─── 2xx ──▶ { ok: true,  expiresAt }
 *                                                                          │
 *                                                                          └── !ok ──▶ { ok: false, status, errorCode?, error }
 *
 * Why this exists:
 *  - Sibling-E §3a Sweep findings (H1, `.tondocs/RELIABILITY_ROADMAP.md`): two
 *    web producers (`cart.tsx` + `use-checkout-session.ts`) hand-assembled
 *    `{ items }` bodies and called `fetch` inline. D2 already pinned the wire
 *    via `satisfies CartReserveRequest`; this extract closes producer↔producer
 *    parity and removes both `knownLegacy` entries from the E6 fitness gate
 *    (`tests/unit/fitness-single-producer-per-endpoint.test.ts`).
 *  - 3rd Built<T> increment (§4 #3c): re-uses the existing
 *    `lib/api/post-json.ts` seam + `seal()` from chat-batch (B1) and
 *    listings-create (F1). No new transport helpers — pure adoption.
 *
 * Web ≠ mobile delivery shape — INTENTIONAL ASYMMETRY (do not "unify"):
 *  - Mobile (`reserveCartStock`) **throws** on `!success` because RN screens
 *    wrap the call in try/catch and surface failures as `Alert`s.
 *  - Web returns a **discriminated union** because `cart.tsx` reads
 *    `errorCode` to drive the Out-Of-Stock modal (`ITEMS_UNAVAILABLE`,
 *    `LISTING_DELETED`, ...) and falls back to `GET /api/cart/availability`
 *    on reject. Mapping `errorCode` from a thrown error would mean
 *    attaching machine-readable fields to an `Error` subclass — strictly
 *    worse ergonomics for the existing OOS-modal flow.
 *  - Both shapes share the same wire contract (`CartReserveRequestSchema`);
 *    only delivery semantics differ. The day a response-side Zod schema
 *    lands (response shape + `ReserveErrorCode` enum migrated into
 *    `@kraahl/contract`), we can revisit unification on BOTH sides.
 *    Out of scope here.
 *
 * Auth — IMPORTANT:
 *  - Web relies on the Supabase **cookie-based** session (route reads it via
 *    `createSupabaseRouteHandlerClient` → `auth.getUser()`). The web builder
 *    therefore does NOT accept an `accessToken` in its intent.
 *  - Mobile passes a Bearer JWT (`opts.accessToken`) because the RN client
 *    has no cookie jar. Do not copy that surface here — it would be dead
 *    code in a browser context.
 *
 * @see app/api/cart/reserve/route.ts — handler with boundary safeParse
 * @see apps/mobile/lib/api-kraahl.ts reserveCartStock — mobile sibling
 *      (request shape mirrored; result type differs by design — see above)
 * @see packages/contract/src/cart-reserve.ts — CartReserveRequestSchema
 * @see lib/chat/chat-send.ts — sibling builder (Sibling-E batch 1)
 * @see lib/listings/listing-create.ts — sibling builder (Sibling-E batch 2 F1)
 * @see lib/api/post-json.ts — Built<T> transport seam
 * @see components/cart/out-of-stock-modal.tsx — consumer of ReserveErrorCode
 *      (re-defined here so a future migration to a response Zod schema lives
 *      in one place; components/ + hooks/ import from this file).
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-E (Sweep findings H1)
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 */

import {
  CartReserveRequestSchema,
  seal,
  type CartReserveRequest,
} from "@kraahl/contract"
import { postJson } from "@/lib/api/post-json"

// ---------------------------------------------------------------------------
// Intent + result types
// ---------------------------------------------------------------------------

/**
 * Machine-readable error codes the route may return. Source of truth lives
 * here in `lib/`; the OOS modal (`components/cart/out-of-stock-modal.tsx`)
 * and the pre-checkout validation hook (`hooks/use-pre-checkout-validation.ts`)
 * import from this file. Migration into `@kraahl/contract` alongside a
 * response Zod schema is an explicit out-of-scope follow-up.
 *
 * Wire ↔ enum: kept in lockstep with the `errorCode` strings returned by
 * `app/api/cart/reserve/route.ts` (see the route's `if (rpcError.message...)`
 * classification + the PROFILE_NOT_FOUND 422 branch). `SELLER_PAYMENT_ERROR`
 * is reserved for a future seller-eligibility classification — already
 * displayed by the OOS modal as a distinct message.
 */
export type ReserveErrorCode =
  | "ITEMS_UNAVAILABLE"
  | "PROFILE_NOT_FOUND"
  | "LISTING_DELETED"
  | "SELLER_PAYMENT_ERROR"
  | "UNKNOWN_ERROR"

export interface ReserveCartIntent {
  items: { listingId: string; quantity: number }[]
}

export type ReserveCartResult =
  | { ok: true; expiresAt: string | null }
  | {
      ok: false
      status: number
      errorCode?: ReserveErrorCode
      error: string
    }

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

/**
 * Build, safeParse, POST `/api/cart/reserve`, and return a discriminated
 * result. Wire shape pinned by `CartReserveRequestSchema` (Sibling-A P1).
 *
 * Failure modes (all surfaced as `{ ok: false }` — never throws):
 *  - Contract reject (empty items, garbage shape) → `status: 400`, no
 *    `errorCode`. Caller's existing OOS modal fallback path handles the
 *    undefined-errorCode case with a generic message — matching the
 *    pre-extract behaviour where a server 400 also yielded no errorCode.
 *  - Server 401 / 422 / 409 / 500 → status + (optional) errorCode + error
 *    string passed straight through. The OOS modal in `cart.tsx` reads
 *    `errorCode` to choose the right copy.
 */
export async function reserveCart(
  intent: ReserveCartIntent,
): Promise<ReserveCartResult> {
  const body = { items: intent.items } satisfies CartReserveRequest

  const parsed = CartReserveRequestSchema.safeParse(body)
  if (!parsed.success) {
    return {
      ok: false,
      status: 400,
      error: parsed.error.issues
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join("; "),
    }
  }

  // Sibling-E §4 #3c: `seal` after safeParse converts the body into a
  // `Built<CartReserveRequest>`. `postJson` accepts ONLY `Built<T>` — a
  // hand-rolled literal in a component cannot reach this seam.
  const res = await postJson(
    "/api/cart/reserve",
    seal<CartReserveRequest>(body),
  )

  const json = (await res.json().catch(() => null)) as
    | {
        success?: boolean
        expiresAt?: unknown
        errorCode?: unknown
        error?: unknown
      }
    | null

  if (res.ok && json?.success === true) {
    return {
      ok: true,
      expiresAt:
        typeof json.expiresAt === "string" ? json.expiresAt : null,
    }
  }

  return {
    ok: false,
    status: res.status,
    errorCode:
      typeof json?.errorCode === "string"
        ? (json.errorCode as ReserveErrorCode)
        : undefined,
    error:
      typeof json?.error === "string"
        ? json.error
        : `Reserve cart (${res.status})`,
  }
}
